/**
 * Industry Pack authoring assist hook.
 * Blueprint patches apply to the right panel automatically (planning draft pattern).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  getPackAuthoringSession,
  sendPackAuthoringMessage,
} from '@/services/aiStudioAgentService';
import type { PackAuthoringPatch } from '@/types/aiStudioAgent';
import type { ConversationMessage, ConversationMessageRole } from '@/types/aiStudio';
import { stripAssistantDisplayContent } from '@/components/ai-studio/conversation/stripAssistantDisplayContent';

function hasPackPatchContent(patch?: PackAuthoringPatch): patch is PackAuthoringPatch {
  if (!patch) return false;
  return Object.entries(patch).some(([, value]) => {
    if (Array.isArray(value)) return value.length > 0;
    return typeof value === 'string' && value.trim().length > 0;
  });
}

function toShellMessages(
  rows: Array<{ id: string; role: string; content: string; createdAt: string }>
): ConversationMessage[] {
  return rows.map((row) => ({
    id: row.id,
    role: row.role as ConversationMessageRole,
    content:
      row.role === 'assistant' ? stripAssistantDisplayContent(row.content) : row.content,
    createdAt: row.createdAt,
  }));
}

export function usePackAuthoring(
  packId: string | undefined,
  enabled: boolean,
  onApplyPatch?: (patch: PackAuthoringPatch) => void
) {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !packId) return;
    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    getPackAuthoringSession(packId)
      .then((session) => {
        if (cancelled) return;
        setMessages(toShellMessages(session.messages));
      })
      .catch(() => {
        if (!cancelled) setErrorKey('aiStudio.packAuthoring.loadFailed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [packId, enabled]);

  const send = useCallback(
    async (text: string) => {
      if (!packId || !text.trim()) return;
      setSending(true);
      setErrorKey(null);
      const optimisticId = `pending-user-${Date.now()}`;
      setMessages((current) => [
        ...current,
        {
          id: optimisticId,
          role: 'user',
          content: text.trim(),
          createdAt: new Date().toISOString(),
        },
        {
          id: `pending-assistant-${Date.now()}`,
          role: 'assistant',
          content: '',
          createdAt: new Date().toISOString(),
          pending: true,
        },
      ]);
      try {
        const result = await sendPackAuthoringMessage(packId, text.trim());
        const patch = result.assistantMessage.suggestedPackPatch;
        const displayContent = stripAssistantDisplayContent(result.assistantMessage.content);

        if (onApplyPatch && hasPackPatchContent(patch)) {
          onApplyPatch(patch);
        }

        setMessages((current) => {
          const withoutPending = current.filter((m) => !m.pending && m.id !== optimisticId);
          return [
            ...withoutPending,
            {
              id: result.userMessage.id,
              role: 'user',
              content: result.userMessage.content,
              createdAt: result.userMessage.createdAt,
            },
            {
              id: result.assistantMessage.id,
              role: 'assistant',
              content: displayContent,
              createdAt: result.assistantMessage.createdAt,
            },
          ];
        });
      } catch {
        setErrorKey('aiStudio.packAuthoring.sendFailed');
        setMessages((current) => current.filter((m) => !m.pending && m.id !== optimisticId));
      } finally {
        setSending(false);
      }
    },
    [packId, onApplyPatch]
  );

  return {
    messages,
    loading,
    sending,
    errorKey,
    send,
  };
}
